import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAllDrivers } from '../../api/driverApi';
import { getAllDriverDeposits } from '../../api/driverDepositApi';
import { addDriverExpense, updateDriverExpense } from '../../api/driverExpenseApi';
import { toast } from 'react-toastify';
import { getCurrencySymbolFromPhone } from "../../utils/currencyFromPhone";

/**
 * AddDriverExpense Component
 * Handles both adding new and editing existing driver expenses
 */
const AddDriverExpense = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const preselectedDriverId = queryParams.get('driver_id');
  const preselectedDepositId = queryParams.get('deposit_id');
  const preselectedExpenseId = queryParams.get('expense_id');
  const preselectedAmount = queryParams.get('amount');
  const preselectedDescription = queryParams.get('description');
  const preselectedDate = queryParams.get('expense_date');

  const [drivers, setDrivers] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState(" ");

  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedDeposit, setSelectedDeposit] = useState(null);

  const [formData, setFormData] = useState({
    driver_id: '',
    deposit_id: '',
    amount: '',
    description: '',
    expense_date: new Date().toISOString().slice(0, 10),
  });

  // Fetch drivers and deposits + preselect if coming from query
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [driverList, depositList] = await Promise.all([
          getAllDrivers(),
          getAllDriverDeposits()
        ]);

        setDrivers(driverList);
        setDeposits(depositList);

        // Auto-select driver
        if (preselectedDriverId) {
          const driver = driverList.find(d => d.driver_id?.toString() === preselectedDriverId);
          if (driver) {
            setSelectedDriver(driver);
            setFormData(prev => ({ ...prev, driver_id: preselectedDriverId }));
          }
        }

        // Auto-select deposit
        if (preselectedDepositId) {
          const deposit = depositList.find(d => d.deposit_id?.toString() === preselectedDepositId);
          if (deposit) {
            setSelectedDeposit(deposit);
            setFormData(prev => ({
              ...prev,
              deposit_id: preselectedDepositId,
              driver_id: preselectedDriverId || deposit.driver_id?.toString(),
            }));
          }
        }

        // Pre-fill expense if editing
        if (preselectedExpenseId) {
          setFormData(prev => ({
            ...prev,
            amount: preselectedAmount || prev.amount,
            description: preselectedDescription ? decodeURIComponent(preselectedDescription) : prev.description,

            expense_date: preselectedDate
              ? preselectedDate.split('T')[0]  // Ensures YYYY-MM-DD
              : prev.expense_date,
          }));
        }

      } catch (error) {
        toast.error('Failed to load drivers or deposits');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  // ✅ When driver is auto-selected (query/edit), set currency symbol too
useEffect(() => {
  if (!formData.driver_id) return;
  if (drivers.length === 0) return;

  const driver = drivers.find(
    (d) => d.driver_id?.toString() === formData.driver_id?.toString()
  );

  const phone = driver?.user?.phone_no || driver?.phone_no;
  setCurrencySymbol(getCurrencySymbolFromPhone(phone) || " ");
}, [formData.driver_id, drivers]);


  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'driver_id') {
      const driver = drivers.find(d => d.driver_id?.toString() === value);
      setSelectedDriver(driver || null);

      const phone = driver?.user?.phone_no || driver?.phone_no;
      setCurrencySymbol(getCurrencySymbolFromPhone(phone) || " ");

      setFormData(prev => ({ ...prev, deposit_id: '' }));
      setSelectedDeposit(null);
    }

    if (name === 'deposit_id') {
      const deposit = deposits.find(d => d.deposit_id?.toString() === value);
      setSelectedDeposit(deposit || null);
    }
  };

  // Calculate deposit balance
  const getDepositBalance = (dep) => {
    return typeof dep.balance !== 'undefined'
      ? parseFloat(dep.balance)
      : parseFloat(dep.petty_cash) - parseFloat(dep.expense || 0);
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (!formData.driver_id || !formData.deposit_id || !formData.amount || !formData.expense_date) {
        toast.error('Please fill all required fields');
        setSubmitting(false);
        return;
      }

      if (preselectedExpenseId) {
        // Update existing expense
        await updateDriverExpense(preselectedExpenseId, {
          ...formData,
          amount: parseFloat(formData.amount),
        });
        toast.success('Expense updated successfully!');
      } else {
        // Add new expense
        await addDriverExpense({
          ...formData,
          amount: parseFloat(formData.amount),
        });
        toast.success('Driver expense added successfully!');
      }

      navigate('/driverdepositcash');
    } catch (error) {
      toast.error(error?.message || 'Failed to save driver expense');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <h2 style={{ marginBottom: '20px' }}>{preselectedExpenseId ? 'Edit Driver Expense' : 'Add Driver Expense'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="row g-3">

          {/* Driver Select */}
          <div className="col-md-6">
            <label className="form-label">Driver <span className="text-danger">*</span></label>
            <select
              className="form-select"
              name="driver_id"
              value={formData.driver_id}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="" disabled>{loading ? 'Loading drivers...' : 'Select a driver'}</option>
              {drivers.map(driver => (
                <option key={driver.driver_id} value={driver.driver_id}>
                  {driver.user?.name || driver.driver_name || driver.name}
                </option>
              ))}
            </select>
          </div>

          {/* Deposit Select */}
          <div className="col-md-6">
            <label className="form-label">Deposit <span className="text-danger">*</span></label>
            <select
              className="form-select"
              name="deposit_id"
              value={formData.deposit_id}
              onChange={handleChange}
              required
              disabled={loading || !formData.driver_id}
            >
              <option value="" disabled>Select a deposit</option>
              {deposits
                .filter(dep => dep.driver_id?.toString() === formData.driver_id)
                .filter(dep => getDepositBalance(dep) > 0)
                .map(dep => (
                  <option key={dep.deposit_id} value={dep.deposit_id}>
                    {dep.deposit_id} - {dep.deposit_date} ({currencySymbol} {parseFloat(dep.petty_cash || 0).toFixed(2)}) | Balance: {currencySymbol} {getDepositBalance(dep).toFixed(2)}

                  </option>
                ))
              }
            </select>
          </div>

          {/* {JSON.stringify(formData)} */}

          {/* Amount Input */}
          <div className="col-md-6">
            <label className="form-label">Amount <span className="text-danger">*</span></label>
            <input
              type="number"
              className="form-control"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="Enter amount"
              required
              min="0"
              step="0.01"
            />
          </div>

          {/* Date */}
          <div className="col-md-6">
            <label className="form-label">Date <span className="text-danger">*</span></label>
            <input
              type="date"
              className="form-control"
              name="expense_date"
              value={formData.expense_date}
              onChange={handleChange}
              required
            />
          </div>

          {/* Description */}
          <div className="col-md-6">
            <label className="form-label">Description</label>
            <input
              type="text"
              className="form-control"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Description (optional)"
            />
          </div>

          {/* Buttons */}
          <div className="col-12 d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-light" onClick={() => navigate(-1)} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>

        </div>
      </form>
    </>
  );
};

export default AddDriverExpense;
